import React from 'react';
import styled from 'styled-components';
import HowToRead, { SectionShell, SubHeading, Panel, Muted, EditButton } from './HowToRead';
import { formatPct, heatmapPages, resolveLabel } from '../reportModel';
import { bandColor } from '../reportPalette';

// Sütunlar 9'luk sayfalara bölünür — yatay kaydırma yok (tek kaydırma kuralı); PDF ile aynı sayfalama.
export const WEB_HEATMAP_COLUMNS = 9;

const Page = styled.div`
  display: grid;
  grid-template-columns: minmax(170px, 1.6fr) repeat(${({ $n }) => $n}, minmax(56px, 1fr));
  gap: 4px;
  margin-bottom: 18px;
  font-size: 13px;
`;

const Head = styled.div`
  padding: 6px 4px;
  font-size: 12px;
  font-weight: 700;
  text-align: center;
  color: ${({ theme }) => theme.colors.report.brand};
  border-bottom: ${({ $line, theme }) => ($line ? `2px solid ${theme.colors.report.brand}` : 'none')};
  grid-column: span ${({ $span }) => $span || 1};
`;

const RowLabel = styled.div`
  padding: 8px 4px;
  font-weight: ${({ $bold }) => ($bold ? 800 : 400)};
  padding-left: ${({ $bold }) => ($bold ? 4 : 16)}px;
`;

const HeatCell = styled.div`
  padding: 8px 4px;
  border-radius: 4px;
  text-align: center;
  font-weight: 700;
`;

const NCell = styled.div`
  padding: 6px 4px;
  text-align: center;
  color: ${({ theme }) => theme.colors.report.muted};
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  align-items: center;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.report.muted};
  i { display: inline-block; width: 12px; height: 12px; border-radius: 2px; margin-right: 6px; vertical-align: middle; }
`;

function cellView(t, group, rowId, threshold) {
  const cell = group.cells ? group.cells[rowId] : null;
  if (group.suppressed || !cell || cell.suppressed || cell.favorablePct == null) {
    return { text: t('survey.report.suppressedCell', { n: threshold }), band: null };
  }
  return { text: formatPct(t, cell.favorablePct), band: cell.band };
}

function HeatValue({ view }) {
  const c = view.band ? bandColor(view.band) : bandColor(null);
  return (
    <HeatCell data-band={view.band || 'none'} style={{ backgroundColor: c.bg, color: c.fg }}>
      {view.text}
    </HeatCell>
  );
}

// Aynı kırılımın ardışık sütunlarını tek üst başlıkta birleştirir.
function spans(columns) {
  const out = [];
  columns.forEach((col) => {
    const last = out[out.length - 1];
    if (last && last.key === col.breakdownKey) last.span += 1;
    else out.push({ key: col.breakdownKey, label: col.label, span: 1 });
  });
  return out;
}

export default function BreakdownHeatmap({ data, narrative, t, canEdit, onEdit }) {
  const { heatmap, threshold } = data;
  const pages = heatmapPages(heatmap, WEB_HEATMAP_COLUMNS);
  // Son inceleme I2 — ısı haritası satırları bölümlerden kurulur; bölümsüz ankette yalnız grup sayıları kalırdı.
  const noRows = pages.length > 0 && !(heatmap.rows || []).length;
  const b = data.method.heatmapBands || [75, 65, 50];
  const p = (v) => formatPct(t, v);

  return (
    <SectionShell id="s04" title={t('survey.report.section.s04')}>
      <HowToRead t={t}>{t('survey.report.help.heatmap', { n: threshold })}</HowToRead>

      {pages.length === 0 ? (
        <Panel data-testid="no-breakdowns">
          <b>{t('survey.report.noBreakdowns')}</b>
          <Muted>{t('survey.report.noBreakdownsHint')}</Muted>
        </Panel>
      ) : noRows ? (
        <Panel data-testid="no-section-rows">
          <Muted>{t('survey.report.noSectionRows')}</Muted>
        </Panel>
      ) : (
        pages.map((columns, pageIndex) => (
          <Page key={pageIndex} $n={columns.length + 1}>
            <Head />
            <Head />
            {spans(columns).map((s, i) => (
              <Head key={`${s.key}-${i}`} $span={s.span} $line>{resolveLabel(t, s.label)}</Head>
            ))}

            <Head />
            <Head>{t('survey.report.overall')}</Head>
            {columns.map((c) => <Head key={`${c.breakdownKey}-${c.group.key}`}>{resolveLabel(t, c.group.label)}</Head>)}

            <RowLabel>{t('survey.report.respondentsN')}</RowLabel>
            <NCell>{heatmap.overall.n}</NCell>
            {columns.map((c) => (
              <NCell key={`n-${c.breakdownKey}-${c.group.key}`}>
                {c.group.suppressed ? t('survey.report.suppressedCell', { n: threshold }) : c.group.n}
              </NCell>
            ))}

            {heatmap.rows.map((row) => (
              <React.Fragment key={row.id}>
                <RowLabel $bold={row.kind === 'dimension'}>{row.name}</RowLabel>
                <HeatValue view={cellView(t, heatmap.overall, row.id, threshold)} />
                {columns.map((c) => (
                  <HeatValue key={`${row.id}-${c.breakdownKey}-${c.group.key}`} view={cellView(t, c.group, row.id, threshold)} />
                ))}
              </React.Fragment>
            ))}
          </Page>
        ))
      )}

      {pages.length > 0 && !noRows && (
        <Legend>
          <span>{t('survey.report.heatLegend')}</span>
          <span><i style={{ backgroundColor: bandColor('b1').bg }} />{t('survey.report.heatAbove', { value: p(b[0]) })}</span>
          <span><i style={{ backgroundColor: bandColor('b2').bg }} />{t('survey.report.heatRange', { from: p(b[1]), to: p(b[0] - 1) })}</span>
          <span><i style={{ backgroundColor: bandColor('b3').bg }} />{t('survey.report.heatRange', { from: p(b[2]), to: p(b[1] - 1) })}</span>
          <span><i style={{ backgroundColor: bandColor('b4').bg }} />{t('survey.report.heatBelow', { value: p(b[2]) })}</span>
        </Legend>
      )}

      {narrative && (
        <Panel>
          <SubHeading style={{ marginTop: 0 }}>
            {t('survey.report.keyDifferences')}
            {canEdit && <EditButton t={t} onClick={() => onEdit('keyDifferences')} testId="edit-keyDifferences" />}
          </SubHeading>
          {narrative.keyDifferences.map((k, i) => <p key={i}>{k}</p>)}
        </Panel>
      )}
    </SectionShell>
  );
}
